import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/** sessionStorage['nav:scroll:<path>'] holds the last `window.scrollY` for that
 *  pathname (filter changes do not create separate scroll keys). */
const SCROLL_PREFIX = 'nav:scroll:';

/**
 * App-level scroll restoration. Saves `window.scrollY` per pathname on
 * scroll (debounced) and restores it after a route change. v1 only
 * tracks the window scroller — pages with custom inner scroll containers
 * reset to top on entry.
 */
export function useScrollRestore(): void {
  const location = useLocation();
  const path = location.pathname;
  const lastPath = useRef<string | null>(null);
  // Set while the retry loop is actively scrolling. The capture
  // listener checks this flag and skips saves — otherwise the
  // scroll events emitted by `scrollTo` during restore would write
  // *intermediate* Y values back to sessionStorage, and the next
  // visit would restore to that partial-progress value (drift of one
  // row's worth of pixels per round-trip).
  const isRestoringRef = useRef(false);

  // Restore on route change. Async-data pages (infinite lists) grow
  // their document height a beat after route change, so a single rAF
  // is too early — the scroll target gets clamped to the not-yet-tall-
  // enough page. We retry each frame until either we land on `y` or
  // ~1s elapses (page just isn't tall enough to need scrolling).
  useEffect(() => {
    if (lastPath.current === path) return;
    lastPath.current = path;
    const saved = sessionStorage.getItem(`${SCROLL_PREFIX}${path}`);
    if (saved == null) {
      // Fresh entry to a page we haven't seen — start at top.
      window.scrollTo(0, 0);
      return;
    }
    const y = Number(saved);
    if (!Number.isFinite(y)) return;
    isRestoringRef.current = true;
    let frames = 0;
    const tick = () => {
      window.scrollTo(0, y);
      if (window.scrollY < y && frames < 60) {
        frames++;
        requestAnimationFrame(tick);
      } else {
        // One extra frame so any trailing scroll events from the last
        // scrollTo land before we re-enable saves.
        requestAnimationFrame(() => { isRestoringRef.current = false; });
      }
    };
    requestAnimationFrame(tick);
  }, [path]);

  // Capture on scroll. Coalesce writes to one per animation frame so
  // we never lag behind the actual scroll position. Skip while the
  // restore loop is running — see isRestoringRef above.
  useEffect(() => {
    let scheduled = false;
    const onScroll = () => {
      if (scheduled || isRestoringRef.current) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        if (isRestoringRef.current) return;
        sessionStorage.setItem(`${SCROLL_PREFIX}${path}`, String(window.scrollY));
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [path]);
}
